<template>
  <div>
    <div class="d-flex align-center">
      <h2>{{ $t('guidance.formItemSettings') }}</h2>
      <v-spacer />
      <v-btn
        @click="openEditDialog()"
        :disabled="loading"
        color="primary"
        variant="flat"
      >
        <v-icon start>mdi-plus</v-icon>
        {{ $t('common.create') }}
      </v-btn>
    </div>

    <!-- table -->
    <div class="mt-6">
      <v-data-table
        :headers="headers"
        :items="itemResponse ? itemResponse.data : []"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- name -->
        <template v-slot:[`item.type`]="{ item }">
          {{ getCategryText(item.type) }}
        </template>

        <!-- createdAt -->
        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <!-- edit -->
        <template v-slot:[`item.edit`]="{ item }">
          <v-btn
            @click="openEditDialog(item)"
            :disabled="loading"
            color="primary"
            variant="outlined"
            small
          >
            {{ $t('common.edit') }}
          </v-btn>
        </template>

        <!-- delete -->
        <template v-slot:[`item.delete`]="{ item }">
          <v-btn
            @click="deleteFormItem(item.id)"
            :disabled="loading"
            color="error"
            small
            variant="flat"
          >
            {{ $t('common.delete') }}
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- dialog -->
    <v-dialog v-model="editDialog" max-width="700px">
      <v-card>
        <v-card-title>{{ $t('guidance.formItem') }}</v-card-title>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="6"> {{ $t('guidance.itemName') }}* </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="input.name"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6"> {{ $t('guidance.itemType') }}* </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.type"
                  :items="categoryItems"
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center" v-if="input.type == 'childText'">
              <v-col cols="6"> {{ $t('common.class') }} </v-col>
              <v-col cols="6">
                <v-select
                  v-model="input.classIdList"
                  :items="classes"
                  item-title="className"
                  item-value="classId"
                  multiple
                  chips
                  variant="outlined"
                  hide-details=""
                  density="compact"
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center" v-if="input.type == 'grid'">
              <v-col cols="6"> {{ $t('guidance.rowsVertical') }} </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="headerInput.rows"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                  :label="$t('guidance.commaSeparated')"
                  placeholder="e.g.) Item1,Item2,..."
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row align="center" v-if="input.type == 'grid'">
              <v-col cols="6"> {{ $t('guidance.colsHorizontal') }} </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="headerInput.cols"
                  variant="outlined"
                  density="compact"
                  hide-details=""
                  :label="$t('guidance.commaSeparated')"
                  placeholder="e.g.) Item1,Item2,..."
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-container>
            <v-row>
              <v-btn @click="editDialog = false" color="grey" text>
                {{ $t('common.cancel') }}
              </v-btn>
              <v-spacer />
              <v-btn
                v-if="isUpdate"
                @click="updateFormItem"
                :disabled="loading"
                color="primary"
                variant="flat"
              >
                {{ $t('common.save') }}
              </v-btn>
              <v-btn
                v-else
                @click="createFormItem"
                :disabled="loading"
                color="primary"
                variant="flat"
              >
                {{ $t('common.create') }}
              </v-btn>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

,
<script lang="ts">
import { defineComponent } from "vue";
import LocalDataService from "@/service/LocalDataService";
import dayjs from "dayjs";
import ApiGuidance from "@/api/ApiGuidance";
import { GuidancePlanFormItemCategoryItems } from "@/constants/guidance";
import {
  GuidancePlanItemCategoryType,
  NurseryClassResponse,
  NurseryGuidancePlanFormItemListResponse,
  NurseryGuidancePlanFormItemResponse,
} from "chaild-api/lib";
import { classModule } from "@/store/dataModules/classModule";

export default defineComponent({
  name: "GuidancePlanFormItemList",

  async mounted() {
    await this.listFormItem();
    await this.listClass();
  },

  data() {
    return {
      loading: false,
      categoryItems: GuidancePlanFormItemCategoryItems,
      headers: [
        { title: this.$t('guidance.itemName'), key: "name" },
        { title: this.$t('guidance.type'), key: "type" },
        { title: this.$t('guidance.createdAt'), key: "createdAt" },
        { title: this.$t('common.edit'), key: "edit" },
        { title: this.$t('common.delete'), key: "delete" },
      ],
      searchFilters: {
        limit: 2000,
        skip: 0,
      },
      itemResponse: null as null | NurseryGuidancePlanFormItemListResponse,
      input: {
        name: "",
        type: "" as GuidancePlanItemCategoryType,
        classIdList: [] as number[],
        itemId: 0 as number,
      },
      headerInput: {
        cols: "",
        rows: "",
      },
      editDialog: false,
    };
  },

  computed: {
    isUpdate: function (): boolean {
      return !!this.input.itemId;
    },
    classes: function (): NurseryClassResponse[] {
      return classModule.classes;
    },
  },

  methods: {
    async listFormItem() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.listPlanFormItem({
          nurseryId,
          ...this.searchFilters,
        }).catch(() => (this.loading = false));
        if (response) {
          this.itemResponse = response;
        }
      }
      this.loading = false;
    },
    async createFormItem() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.createPlanFormItem({
          nurseryId,
          ...this.input,
          option:
            this.input.type == "grid"
              ? {
                  header: {
                    cols: this.getHeaderArray(this.headerInput.cols),
                    rows: this.getHeaderArray(this.headerInput.rows),
                  },
                }
              : undefined,
        }).catch(() => (this.loading = false));
        if (response) {
          this.editDialog = false;
          this.clearInput();
          await this.listFormItem();
        }
      }
      this.loading = false;
    },
    async updateFormItem() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        const response = await ApiGuidance.updatePlanFormItem({
          nurseryId,
          ...this.input,
          option:
            this.input.type == "grid"
              ? {
                  header: {
                    cols: this.getHeaderArray(this.headerInput.cols),
                    rows: this.getHeaderArray(this.headerInput.rows),
                  },
                }
              : undefined,
        }).catch(() => (this.loading = false));
        if (response) {
          this.editDialog = false;
          this.clearInput();
          await this.listFormItem();
        }
      }
      this.loading = false;
    },
    async deleteFormItem(itemId: number) {
      const answer = confirm(this.$t('guidance.deleteConfirm'));
      if (answer) {
        this.loading = true;
        const nurseryId = LocalDataService.getNurseryId();
        if (nurseryId && itemId) {
          const response = await ApiGuidance.deletePlanFormItem({
            nurseryId,
            itemId,
          }).catch(() => (this.loading = false));
          if (response) {
            alert(this.$t('guidance.deletedSuccessfully'));
            await this.listFormItem();
          }
        }
        this.loading = false;
      }
    },
    async listClass() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule
          .listClass({
            nurseryId,
          })
          .catch();
      }
    },
    openEditDialog(value?: NurseryGuidancePlanFormItemResponse) {
      if (value) {
        this.setInput(value);
      } else {
        this.clearInput();
      }
      this.editDialog = true;
    },
    getCategryText(value: string): string {
      const item = this.categoryItems.find((c) => c.value == value);
      return item?.text || "";
    },
    clearInput() {
      this.input = {
        name: "",
        type: "text",
        classIdList: [],
        itemId: 0,
      };
      this.headerInput = {
        cols: "",
        rows: "",
      };
    },
    setInput(value: NurseryGuidancePlanFormItemResponse) {
      this.input = {
        name: value.name,
        type: value.type,
        classIdList: value.classList?.map((c) => c.classId) || [],
        itemId: value.id,
      };
      if (value.type == "grid" && value.option) {
        this.headerInput = {
          cols: value.option.header.cols.join(","),
          rows: value.option.header.rows.join(","),
        };
      }
    },
    getHeaderArray(value: string): string[] {
      if (value) {
        return value.split(",");
      } else {
        return [];
      }
    },
    formatDate(value: string): string {
      if (!value) {
        return "";
      }
      return dayjs(value).format("DD/MM/YYYY");
    },
  },
});
</script>
